import { AgGridCellTooltipComponent, AgGridCellTooltipValueGetter } from 'components/AgGrid/AgGridCellTooltipComponent';
import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import type { CatalogRatioData, CatalogRatioShortData, PriceListRatioRowData } from 'gql/graphql';
import { SortEnumType, RatioTypeEnum } from 'gql/graphql';
import { formatNumberToRu } from 'utils';

import type { ColDef } from 'ag-grid-community';
import type React from 'react';
import { useMemo } from 'react';

import { addRatiosModalCodeFilterGql, addRatiosModalNameFilterGql } from '../addRatiosModalQueries';
import { CellRenderCheckbox } from '../components';

interface IColumnsAddCoefficient {
    data: CatalogRatioData[] | null;
    checkedIds: Record<string, CatalogRatioShortData | null>;
    changeFilterHandler: (filterData: any) => void;
    handleChangeCheckbox: (row: PriceListRatioRowData) => ({ checked }: { checked: boolean }) => void;
    handleAllChangeCheckbox: (data: Record<string, boolean>) => () => void;
    ratioType: RatioTypeEnum;
    ratioGroupId?: string | number;
    canCheckGroupRow: boolean;
    canCheckRow: boolean;
    isConstructionWorksGroupSelected: boolean;
    subGroupsNames: string[];
}

const numericRatioFieldConfig = {
    valueFormatter: ({ value }: { value: string }) => formatNumberToRu(value, 4) || '',
    cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 4) || '',
};

export const useAddRatiosModalColumns = ({
    data,
    checkedIds,
    changeFilterHandler,
    handleChangeCheckbox,
    handleAllChangeCheckbox,
    ratioType,
    ratioGroupId,
    canCheckGroupRow,
    canCheckRow,
    isConstructionWorksGroupSelected,
    subGroupsNames,
}: IColumnsAddCoefficient) => {
    const isHeadCheckbox = true;

    const filterWhereVariables = useMemo(
        () => ({
            ratioType: {
                eq: ratioType,
            },
            ...(ratioType !== RatioTypeEnum.TechPartCoef && !isConstructionWorksGroupSelected
                ? {
                      catalogRatioGroupId: { eq: ratioGroupId },
                  }
                : {}),
            ...(ratioType === RatioTypeEnum.DismantlingCoef && isConstructionWorksGroupSelected && subGroupsNames.length
                ? {
                      and: [
                          {
                              catalogRatioGroupName: {
                                  in: subGroupsNames,
                              },
                          },
                      ],
                  }
                : {}),
        }),
        [isConstructionWorksGroupSelected, ratioGroupId, ratioType, subGroupsNames],
    );

    const finalColumns = useMemo<ColDef[]>(
        () => [
            {
                headerName: '',
                field: 'checkbox',
                width: 46,
                cellClass: 'firstColumn',
                spanHeaderHeight: true,
                autoHeight: true,
                headerComponent: CellRenderCheckbox,
                headerComponentParams: {
                    checkedIds,
                    ratioType,
                    handleChangeCheckbox,
                    handleAllChangeCheckbox,
                    currentRowData: data,
                    isHeadCheckbox,
                },
                cellRenderer: CellRenderCheckbox,
                cellRendererParams: {
                    checkedIds,
                    ratioType,
                    handleChangeCheckbox,
                    handleAllChangeCheckbox,
                    currentRowData: data,
                    canCheckGroupRow,
                    canCheckRow,
                },
            },
            {
                headerName: 'Шифр',
                field: 'code',
                initialWidth: 130,
                minWidth: 100,
                cellRenderer: ({ value }: { value: string }) => value || '',
                ...((ratioType === RatioTypeEnum.TechPartCoef || !!ratioGroupId) && {
                    filter: AgGridCustomFilterAdapter,
                    filterParams: {
                        dataAccessor: 'catalogRatios',
                        customRequestId: 'addRatiosModalCodeFilter',
                        query: addRatiosModalCodeFilterGql,
                        additionalWhereVariables: filterWhereVariables,
                        variables: {
                            order: [
                                {
                                    code: SortEnumType.Asc,
                                },
                            ],
                            take: 1000,
                        },
                        whereVariables: ['code'],
                        childFieldAccessor: 'code',
                        columns: [
                            {
                                headerName: 'Шифр',
                                field: 'code',
                                checkboxSelection: true,
                                width: 400,
                            },
                        ],
                        changeFilterHandler,
                    },
                }),
            },
            {
                headerName: 'Наименование',
                field: 'name',
                initialWidth: 400,
                minWidth: 200,
                tooltipComponent: AgGridCellTooltipComponent,
                tooltipValueGetter: AgGridCellTooltipValueGetter({}),
                cellClass: 'ellipsis',
                spanHeaderHeight: true,
                cellRenderer: ({ value }: { value: string }) => <div>{value || ''}</div>,
                ...((ratioType === RatioTypeEnum.TechPartCoef || !!ratioGroupId) && {
                    filter: AgGridCustomFilterAdapter,
                    filterParams: {
                        dataAccessor: 'catalogRatios',
                        customRequestId: 'addRatiosModalNameFilter',
                        query: addRatiosModalNameFilterGql,
                        additionalWhereVariables: filterWhereVariables,
                        variables: {
                            order: [
                                {
                                    name: SortEnumType.Asc,
                                },
                            ],
                            take: 1000,
                        },
                        whereVariables: ['name'],
                        childFieldAccessor: 'name',
                        columns: [
                            {
                                headerName: 'Наименование',
                                field: 'name',
                                checkboxSelection: true,
                                width: 400,
                            },
                        ],
                        changeFilterHandler,
                    },
                }),
            },
            {
                headerName: 'К озп',
                field: 'kozp',
                autoHeight: true,
                align: 'center',
                initialWidth: 90,
                minWidth: 80,
                ...numericRatioFieldConfig,
            },
            {
                headerName: 'К эмм',
                field: 'kem',
                align: 'center',
                autoHeight: true,
                initialWidth: 90,
                minWidth: 80,
                ...numericRatioFieldConfig,
            },
            {
                headerName: 'К нр',
                field: 'knr',
                autoHeight: true,
                align: 'center',
                initialWidth: 90,
                minWidth: 80,
                ...numericRatioFieldConfig,
            },
            {
                headerName: 'К сп',
                field: 'ksp',
                align: 'center',
                autoHeight: true,
                initialWidth: 90,
                minWidth: 80,
                ...numericRatioFieldConfig,
            },
            {
                headerName: 'К мат',
                autoHeight: true,
                field: 'kmat',
                align: 'center',
                initialWidth: 90,
                minWidth: 80,
                ...numericRatioFieldConfig,
            },
            {
                headerName: 'К тз',
                field: 'ktz',
                autoHeight: true,
                align: 'center',
                initialWidth: 90,
                minWidth: 80,
                ...numericRatioFieldConfig,
            },
            {
                headerName: 'К тз зпм',
                field: 'ktzzpm',
                autoHeight: true,
                align: 'center',
                initialWidth: 100,
                minWidth: 90,
                ...numericRatioFieldConfig,
            },
        ],
        [
            ratioType,
            checkedIds,
            handleChangeCheckbox,
            handleAllChangeCheckbox,
            data,
            isHeadCheckbox,
            ratioGroupId,
            canCheckGroupRow,
            canCheckRow,
            filterWhereVariables,
            changeFilterHandler,
        ],
    );

    return {
        finalColumns,
    };
};
